/**
 * Axios Interceptor Configuration
 * Automatically adds auth tokens to requests and handles token refresh
 */

import axios from 'axios';
import authService from './authService';

let isRefreshing = false;
let failedQueue = [];
let requestInterceptorId = null;
let responseInterceptorId = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Setup axios interceptors for authentication
 * @param {function} onTokenExpired - Callback when token expires
 * @param {string} backendUrl - Backend API URL
 */
export const setupAxiosInterceptors = (onTokenExpired, backendUrl) => {
  // Request interceptor - Add auth token to all requests
  requestInterceptorId = axios.interceptors.request.use(
    (config) => {
      const token = authService.getToken();

      // Allow callers to skip adding Authorization header (e.g., refresh endpoint)
      if (config && config._skipAuth) return config;

      if (!token) {
        console.warn('No token available for request:', config.url);
      }

      if (token && !config.headers['Authorization']) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - Handle token expiration and refresh
  responseInterceptorId = axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      // Ensure headers object exists to avoid mutations on undefined
      if (originalRequest && !originalRequest.headers) {
        originalRequest.headers = {};
      }
      console.debug('[axiosInterceptor] response error intercepted', {
        status: error.response?.status,
        url: originalRequest?.url,
        method: originalRequest?.method,
        retryFlag: originalRequest?._retry,
        isRefreshing,
        queueLength: failedQueue.length,
      });

      // Don't try to refresh token for auth endpoints (login, refresh) to avoid loops
      // Login 401 = invalid credentials, not expired token
      const isAuthEndpoint =
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/login');

      // If error is 401 and we haven't tried to refresh yet
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !isAuthEndpoint
      ) {
        // If already refreshing, queue this request
        if (isRefreshing) {
          console.debug(
            '[axiosInterceptor] already refreshing - queueing request',
            { url: originalRequest.url }
          );
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return axios(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = authService.getRefreshToken();
        console.log(
          '[axiosInterceptor] 401 received - attempting token refresh, has refreshToken:',
          !!refreshToken,
          'originalUrl:',
          originalRequest.url
        );

        if (!refreshToken) {
          // No refresh token, redirect to login
          console.warn('No refresh token available, logging out');
          authService.clearToken();
          authService.setLogoutFlag();
          isRefreshing = false;
          if (onTokenExpired) {
            onTokenExpired();
          }
          return Promise.reject(error);
        }

        try {
          // Attempt to refresh token - don't include Authorization header
          console.debug(
            '[axiosInterceptor] sending refresh request to',
            `${backendUrl}/api/v1/auth/refresh`
          );
          const response = await axios.post(
            `${backendUrl}/api/v1/auth/refresh`,
            {
              refreshToken: refreshToken,
            },
            {
              // custom flag to instruct request interceptor not to attach the Authorization header
              _skipAuth: true,
            }
          );

          const { jwt, refreshToken: newRefreshToken } = response.data;

          if (jwt) {
            console.log('[axiosInterceptor] Token refreshed successfully');
            authService.setToken(jwt);

            if (newRefreshToken) {
              authService.setRefreshToken(newRefreshToken);
            }

            // Update authorization header
            axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
            if (!originalRequest.headers) originalRequest.headers = {};
            originalRequest.headers['Authorization'] = `Bearer ${jwt}`;

            // Process queued requests
            processQueue(null, jwt);

            isRefreshing = false;

            // Retry original request
            return axios(originalRequest);
          } else {
            // No JWT in response
            throw new Error('No JWT in refresh response');
          }
        } catch (refreshError) {
          console.error(
            '[axiosInterceptor] Token refresh failed:',
            refreshError?.response?.status,
            refreshError?.response?.data,
            refreshError
          );
          processQueue(refreshError, null);
          isRefreshing = false;

          // Refresh failed, clear tokens and redirect
          authService.clearToken();
          authService.setLogoutFlag();

          if (onTokenExpired) {
            console.debug('[axiosInterceptor] calling onTokenExpired callback');
            onTokenExpired();
          }

          return Promise.reject(refreshError);
        }
      }

      // If we get here, it's a 401 that couldn't be refreshed or other error
      // For auth endpoints returning 401, handle appropriately
      if (error.response?.status === 401) {
        const isLoginEndpoint = originalRequest.url?.includes('/auth/login');
        const isRefreshEndpoint =
          originalRequest.url?.includes('/auth/refresh');

        if (isLoginEndpoint) {
          // Login failed with 401 = invalid credentials
          // Don't redirect to logout, just let the error propagate to show login error
          console.debug(
            '[axiosInterceptor] login 401 - invalid credentials, not redirecting'
          );
        } else if (isRefreshEndpoint) {
          // Refresh endpoint failed - token is truly invalid, clear everything
          authService.clearToken();
          authService.setLogoutFlag();
          isRefreshing = false;

          if (onTokenExpired) {
            console.debug(
              '[axiosInterceptor] refresh endpoint failed, calling onTokenExpired'
            );
            onTokenExpired();
          }
        } else if (!originalRequest._retry) {
          // Other 401 that wasn't retried (no refresh token)
          authService.clearToken();
          authService.setLogoutFlag();

          if (onTokenExpired) {
            console.debug(
              '[axiosInterceptor] non-auth 401, calling onTokenExpired'
            );
            onTokenExpired();
          }
        }
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Clear all axios interceptors (useful for testing or cleanup)
 */
export const clearAxiosInterceptors = () => {
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }
};

export default setupAxiosInterceptors;
